// components/ResetPassword.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardHeader, CardContent, CardFooter } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Label } from "../ui/label";
import { useToast } from "../ui/use-toast";
import { EndpointKeys } from "@/config/api";
import type { ConfirmResetRequest, ConfirmResetResponse } from "@/types/auth";
import ApiService from "@/services/api";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { z } from "zod";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const passwordSchema = z.object({
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof passwordSchema>;

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  const form = useForm<FormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setIsValidToken(false);
      setIsValidating(false);
      return;
    }
    
    try {
      setIsValidating(true);
      const response = await ApiService.request<{ valid: boolean }>(
        EndpointKeys.VALIDATE_RESET_TOKEN,
        {
          skipAuth: true,
          urlParams: { token },
        },
      );
      setIsValidToken(response.valid);
    } catch (err) {
      setIsValidToken(false);
    } finally {
      setIsValidating(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await ApiService.request<ConfirmResetResponse>(
        EndpointKeys.CONFIRM_PASSWORD_RESET,
        {
          method: "POST",
          body: {
            token,
            password: values.password,
          } as ConfirmResetRequest,
          skipAuth: true,
        },
      );

      toast({
        title: "Password Reset Successful",
        description: "Your password has been reset. You can now log in with your new password.",
        variant: "default",
      });

      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      toast({
        title: "Error Resetting Password",
        description: err instanceof Error
          ? err.message
          : "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50">
        <Card className="w-full max-w-md shadow-md">
          <CardContent className="pt-6 px-6">
            <div className="flex flex-col items-center justify-center space-y-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-center text-muted-foreground">Validating your reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50">
        <Card className="w-full max-w-md shadow-md">
          <CardContent className="space-y-6 pt-6 px-6">
            <div className="flex flex-col items-center justify-center py-4">
              <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
              <h1 className="text-xl font-semibold text-center mb-2">Invalid Reset Link</h1>
              <p className="text-center text-muted-foreground">
                This password reset link is invalid or has expired. Please
                request a new password reset.
              </p>
            </div>
            <Button className="w-full" onClick={() => navigate("/login")}>
              Return to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader>
          <div className="flex flex-col items-center space-y-2">
            <h1 className="text-2xl font-bold text-center">Reset Your Password</h1>
            <p className="text-center text-muted-foreground">Enter your new password below</p>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password" 
                        placeholder="Enter your new password" 
                        className="w-full"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password" 
                        placeholder="Confirm your new password" 
                        className="w-full"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full mt-6" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting Password...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center border-t p-4">
          <Button variant="link" onClick={() => navigate("/login")}>
            Back to Login
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ResetPassword;
